/**
 * reCAPTCHA Enterprise, score-based — the "invisible" flavour: no checkbox and no
 * image puzzle. The page asks Google for a token in the background and hands it to
 * the backend, which calls `projects.assessments.create` and decides what the score
 * is worth. Nothing here is a security boundary on its own: a token that never
 * reaches the server, or one the server does not verify, protects nobody.
 *
 * The script is only fetched when a screen actually needs a token (see
 * `loginAttempts.ts`), so an ordinary first-try sign-in costs no extra request.
 */

declare global {
  interface Window {
    grecaptcha?: {
      enterprise?: {
        ready: (callback: () => void) => void
        execute: (siteKey: string, options: { action: string }) => Promise<string>
      }
    }
  }
}

/** Site keys are public by design — they are readable in any page that renders the
 *  widget and are locked to a domain list in the Google Cloud console. The env var
 *  stays as the escape hatch for pointing a branch build at another project. */
const DEFAULT_SITE_KEY = '6Lc-JrQtAAAAALcOdLAdmZicEWaLqCskgVL340C0'

export const RECAPTCHA_SITE_KEY = import.meta.env.VITE_RECAPTCHA_SITE_KEY || DEFAULT_SITE_KEY

export const recaptchaAvailable = RECAPTCHA_SITE_KEY.length > 0

const SCRIPT_ID = 'recaptcha-enterprise'

let loading: Promise<void> | null = null

/** Injects the enterprise script once per page load. Safe to call on every render. */
export function loadRecaptcha(): Promise<void> {
  if (!recaptchaAvailable) return Promise.reject(new Error('reCAPTCHA site key is not configured'))
  if (loading) return loading

  loading = new Promise<void>((resolve, reject) => {
    if (window.grecaptcha?.enterprise) {
      resolve()
      return
    }

    const existing = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null
    const script = existing ?? document.createElement('script')
    script.addEventListener('load', () => resolve(), { once: true })
    script.addEventListener('error', () => reject(new Error('reCAPTCHA failed to load')), { once: true })

    if (!existing) {
      script.id = SCRIPT_ID
      script.async = true
      script.defer = true
      script.src = `https://www.google.com/recaptcha/enterprise.js?render=${RECAPTCHA_SITE_KEY}`
      document.head.appendChild(script)
    }
  }).catch((error: unknown) => {
    // A failed load must be retryable: a customer on a flaky connection should not
    // be stuck behind a script that will never come back.
    loading = null
    throw error
  })

  return loading
}

/**
 * Resolves to a fresh token for `action`, or `null` when reCAPTCHA could not run —
 * blocked script, offline, ad blocker. Failing open here is deliberate: the client
 * cannot be trusted to enforce anything anyway, and a network hiccup must not lock a
 * real customer out of their account. The backend is the one that decides whether a
 * request with no token, after repeated failures, is acceptable.
 */
export async function executeRecaptcha(action: string): Promise<string | null> {
  if (!recaptchaAvailable) return null

  try {
    await loadRecaptcha()
    const enterprise = window.grecaptcha?.enterprise
    if (!enterprise) return null

    await new Promise<void>((resolve) => enterprise.ready(resolve))
    return await enterprise.execute(RECAPTCHA_SITE_KEY, { action })
  } catch {
    return null
  }
}
